import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { useMutation } from '@apollo/client';
import { START_HOME_SESSION } from '../../../../graphql/mutations';

import { Fields, Button } from '@ets-global/b2c-website-ui';

import { useDateToString } from '../../../Element/DateToString';
import { dateToISOString } from '../../../../helpers/Formatter/DateFormatter';
import { env } from '../../../../helpers/env';
import { useDataLayer, useRouter } from '../../../../hooks';
import useYup from '../../../../hooks/useYup';
import { convertMinutesToHours } from '../../../../helpers/convertMinutesToHours';
import { isCurrentlyInsideItsMaintenanceWindow } from '../../../../helpers/isCurrentlyInsideItsMaintenanceWindow';
import ConfirmModal from '../../../Base/Modal/ConfirmModal';
import './styles.scss';

const ECLegalSentenceForHomeTestBlock = ({
  title,
  sessionIsStarted,
  sessionIsFinished,
  testDuration,
  candidateName,
  candidateBirthDate,
  candidateBirthPostCode,
  homeSessionId,
  nextItsMaintenanceWindow,
  toeicLinkRedirect,
  certifCpf
}) => {
  const DateToString = useDateToString();
  const { lang } = useRouter();
  const { Yup } = useYup();
  const { t } = useTranslation();
  const { pushEventGAClickNavigation } = useDataLayer();
  const [startHomeSession] = useMutation(START_HOME_SESSION);
  const [modalOptions, setModalOptions] = useState({
    type: 'default',
    isOpened: false,
  });

  const defaultDate = new Date();
  defaultDate.setFullYear(1999);

  const [isInMaintenanceWindow, setIsInMaintenanceWindow] = useState(false);
  const testDurationInHours = convertMinutesToHours(testDuration);
  const candidateInfoFormRef = useRef(null);

  useEffect(() => {
    if (sessionIsFinished) {
      setModalOptions({ type: 'default', isOpened: true });
    }
    const interval = setInterval(() => handleMaintenanceWindow(), 1000);

    return () => clearInterval(interval);
  }, []);

  const redirectToTest = () => {
    window.location = toeicLinkRedirect ? toeicLinkRedirect : env('RAZZLE_ITS_REDIRECTION_URL');
  };

  const handleMaintenanceWindow = () => {
    setIsInMaintenanceWindow(isCurrentlyInsideItsMaintenanceWindow(testDurationInHours, nextItsMaintenanceWindow));
  };

  const isStartSessionModal = () => {
    return modalOptions.type === 'start-session';
  };

  const shouldCollectCandidateInfoForToeicRedirect = () => {
    return isStartSessionModal() && Boolean(toeicLinkRedirect) && certifCpf === true;
  };

  const getModalTitle = () => {
    switch (modalOptions.type) {
      case 'start-session': {
        return 'toeic-in-autonomy.modal-start-session.title';
      }
      case 'CANNOT_START_DUE_TO_ONGOING_ITS_MAINTENANCE_WINDOW': {
        return 'toeic-in-autonomy.modal-its-maintenance-window.title';
      }
      default:
        return 'toeic-in-autonomy.modal.title';
    }
  };

  const getModalMessage = () => {
    switch (modalOptions.type) {
      case 'start-session': {
        if (toeicLinkRedirect) {
          return 'toeic-in-autonomy.modal-start-session.description-with-duration-singular-toeic-link';
        }

        return 'toeic-in-autonomy.modal-start-session.description-with-duration-singular';
      }
      case 'CANNOT_START_DUE_TO_ONGOING_ITS_MAINTENANCE_WINDOW': {
        return 'toeic-in-autonomy.modal-its-maintenance-window.description';
      }
      default:
        return 'toeic-in-autonomy.modal.description-with-duration';
    }
  };

  const toggleModal = () => {
    setModalOptions({
      ...modalOptions,
      isOpened: !modalOptions?.isOpened,
    });
  };

  const startSession = async () => {
    pushEventGAClickNavigation({ eventLabel: 'start the test' });

    let candidateInfoVariables = {};
    if (shouldCollectCandidateInfoForToeicRedirect()) {
      const candidateInfoValues = candidateInfoFormRef.current.state.values;
      const errors = await candidateInfoFormRef.current.validateForm(candidateInfoValues);
      candidateInfoFormRef.current.setTouched({ birthDate: true, postalCode: true });

      if (Object.keys(errors).length > 0) {
        return;
      }

      candidateInfoVariables = {
        candidateBirthDate: dateToISOString(new Date(candidateInfoValues.birthDate)),
        candidateBirthPostCode: candidateInfoValues.postalCode,
      };
    }

    startHomeSession({
      variables: {
        id: homeSessionId,
        ...candidateInfoVariables,
      },
      onCompleted: ({ startHomeSessionResult: { hasSucceeded, reason } }) => {
        toggleModal();
        if (hasSucceeded) {
          redirectToTest();

          return;
        }

        if (reason) {
          setModalOptions({ type: reason, isOpened: true });

          return;
        }

        setModalOptions({ type: 'default', isOpened: true });
      },
    });
  };

  const handleSubmit = (data, actions) => {
    actions.setSubmitting(false);
    if (sessionIsFinished) {
      setModalOptions({ type: 'default', isOpened: true });

      return;
    }
    if (!sessionIsStarted) {
      setModalOptions({ type: 'start-session', isOpened: true });

      return;
    }
    redirectToTest();
  };

  const validationSchema = Yup.object().shape({
    agreement: Yup.boolean()
      .required()
      .test('isChecked', t('toeic-in-autonomy.form.agreement-error-message'), (value) => value === true),
  });

  const candidateInfoValidationSchema = Yup.object().shape({
    birthDate: Yup.date().required(t('toeic-in-autonomy.modal-start-session.form.birth-date-error-message')),
    postalCode: Yup.string()
      .required(t('toeic-in-autonomy.modal-start-session.form.postal-code-error-message'))
      .max(10),
  });

  let modalButtons = [{ label: t('common.ok'), onClick: () => toggleModal() }];
  if (isStartSessionModal()) {
    modalButtons = [
      { label: t('common.refuse'), className: 'button--transparent-light', onClick: () => toggleModal() },
      { label: t('common.accept'), disabled: isInMaintenanceWindow, onClick: () => startSession() },
    ];
  }

  return (
    <Fragment>
      <div className="ec-legal-sentance-for-home-test-block">
        <div className="container container--padding">
          <p className="ec-legal-sentance-for-home-test-block__title">{title}</p>
          <Formik initialValues={{ agreement: false }} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ isSubmitting }) => (
              <Form>
                <Fields.FormikFastField
                  id="agreement"
                  name="agreement"
                  component={Fields.CheckboxField}
                  label={t('toeic-in-autonomy.form.label.legal_sentence', { fullName: candidateName })}
                  required={true}
                />
                <Button aria-label="submit form" type="submit" disabled={isSubmitting || isInMaintenanceWindow}>
                  <Trans>toeic-in-autonomy.form.button.{sessionIsStarted ? 'started' : 'not_started'}</Trans>
                </Button>
                {isInMaintenanceWindow && (
                  <p
                    className="ec-legal-sentance-for-home-test-block__its-maintenance-window-error"
                    dangerouslySetInnerHTML={{
                      __html: t('toeic-in-autonomy.maintenance-window.error', {
                        endDate: DateToString({
                          date: nextItsMaintenanceWindow.endDate,
                          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                          format: 'Pp',
                        }),
                      }),
                    }}
                  ></p>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <ConfirmModal
        isOpen={modalOptions.isOpened}
        title={t(getModalTitle())}
        onClose={() => toggleModal()}
        buttons={modalButtons}
      >
        <Trans count={testDuration} values={{ homeTestDuration: testDuration }}>
          {getModalMessage()}
        </Trans>
        {shouldCollectCandidateInfoForToeicRedirect() && (
          <Formik
            ref={candidateInfoFormRef}
            initialValues={{ birthDate: candidateBirthDate || '', postalCode: candidateBirthPostCode || '' }}
            validationSchema={candidateInfoValidationSchema}
            onSubmit={() => {}}
          >
            {({ setFieldValue }) => (
              <Form className="ec-legal-sentance-for-home-test-block__candidate-info-form">
                <Fields.FormikFastField
                  icon="calendar"
                  component={Fields.DatePickerField}
                  id="birthDate"
                  name="birthDate"
                  label={t('toeic-in-autonomy.modal-start-session.form.label.birth-date')}
                  required={true}
                  maxDate={'today'}
                  setFieldValue={(value) => setFieldValue('birthDate', value)}
                  calendarDefaultDate={defaultDate}
                />
                <Fields.FormikFastField
                  id="postalCode"
                  name="postalCode"
                  component={Fields.TextField}
                  label={t('toeic-in-autonomy.modal-start-session.form.label.postal-code')}
                  autoComplete={'postal-code'}
                  required={true}
                />
              </Form>
            )}
          </Formik>
        )}
      </ConfirmModal>
    </Fragment>
  );
};

ECLegalSentenceForHomeTestBlock.propTypes = {
  title: PropTypes.string.isRequired,
  sessionIsStarted: PropTypes.bool.isRequired,
  sessionIsFinished: PropTypes.bool.isRequired,
  testDuration: PropTypes.number.isRequired,
  candidateName: PropTypes.string.isRequired,
  candidateBirthDate: PropTypes.string,
  candidateBirthPostCode: PropTypes.string,
  homeSessionId: PropTypes.number.isRequired,
  nextItsMaintenanceWindow: PropTypes.object,
  toeicLinkRedirect: PropTypes.string,
  certifCpf: PropTypes.bool,
};

export default ECLegalSentenceForHomeTestBlock;
